import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, X, LockKey, Eye, EyeSlash, Copy, SignIn, SignOut, UserCircleGear, Printer, ArrowsDownUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, usePermissions } from "@/App";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { metFirma } from "@/lib/downloadUrl";
import DocumentenKluis from "@/components/DocumentenKluis";
import { MedewerkerVolgorde } from "@/components/MedewerkerVolgorde";
import { useLoonZichtbaar } from "@/components/LoonSlot";
import { CtnEmployeeAccess } from "@/components/ctn/CtnEmployeeAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  ctn_toegang: "rol",
  voornaam: "",
  achternaam: "",
  email: "",
  telefoon: "",
  uurloon: "",
  functie: "",
  pincode: "",
  filiaal_ids: [],
  startdatum: "",
  verlof_recht: "20",
  adres: "",
  rijksregisternr: "",
  iban: "",
  bic: "",
  geboortedatum: "",
  nationaliteit: "",
  eid_kaartnummer: "",
  eid_geldig_tot: "",
  eid_foto_voor: "",
  eid_foto_achter: "",
  rijbewijs_categorieen: "",
  rijbewijs_nummer: "",
  rijbewijs_geldig_tot: "",
  rijbewijs_foto_voor: "",
  rijbewijs_foto_achter: "",
  kleur: "",
  zondag_extra_per_4u: "",
  feestdag_extra_uren: "",
};

export const NAAM_KLEUREN = ["#C62828", "#EF6C00", "#F9A825", "#2E7D32", "#00838F", "#002FA7", "#6A1B9A", "#AD1457", "#424242"];

export default function Medewerkers({ medewerkers, filialen, onRefresh, loading }) {
  const navigate = useNavigate();
  const user = useAuth();
  const perms = usePermissions();
  const role = user?.role || "medewerker";
  const canEdit = perms[role]?.medewerkers_bewerken === true;
  const canKlokAnderen = perms[role]?.klokken_anderen === true;
  const loonZichtbaar = useLoonZichtbaar();
  const canLonen = perms[role]?.lonen_zien === true && loonZichtbaar;
  const isSelfOnly = role === "medewerker"; // medewerkers zien alleen eigen rij
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [medContracten, setMedContracten] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [ctnAccessConfig, setCtnAccessConfig] = useState(null);
  useEffect(() => {
    let active = true;
    setCtnAccessConfig(null);
    if (dialogOpen && canEdit) axios.get(`${API}/medewerkers/ctn-toegang`).then(({ data }) => {
      if (active) setCtnAccessConfig(data);
    }).catch(() => { if (active) setCtnAccessConfig(null); });
    return () => { active = false; };
  }, [dialogOpen, canEdit]);

  // Laad arbeidscontracten van de bewerkte medewerker
  useEffect(() => {
    if (!editing?.id) {
      setMedContracten([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/contracten`, {
        params: { medewerker_id: editing.id },
      })
      .then((res) => setMedContracten(res.data || []))
      .catch(() => setMedContracten([]));
  }, [editing?.id]);
  const [filterFiliaal, setFilterFiliaal] = useState("all");
  const [filterFunctie, setFilterFunctie] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all"); // all | actief | niet-actief
  const [filterFiliaalKoppeling, setFilterFiliaalKoppeling] = useState("all"); // all | met | zonder | meerdere
  const [uurloonMin, setUurloonMin] = useState("");
  const [uurloonMax, setUurloonMax] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [revealedPins, setRevealedPins] = useState({});
  const [activeIds, setActiveIds] = useState(new Set());
  const [activeMap, setActiveMap] = useState({}); // medewerker_id -> {filiaal_id, filiaal_naam, record_id}
  const [processingId, setProcessingId] = useState(null);
  const [selectFiliaalFor, setSelectFiliaalFor] = useState(null); // medewerker_id voor wie we filiaal selecteren
  const [docWaarschuwingen, setDocWaarschuwingen] = useState({}); // medewerker_id -> {verlopen, binnenkort}
  const [volgordeOpen, setVolgordeOpen] = useState(false);

  const loadDocWaarschuwingen = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/documenten/verlopend`, { params: { dagen: 60 } });
      const map = {};
      (r.data || []).forEach((d) => {
        const huidig = map[d.medewerker_id] || { verlopen: 0, binnenkort: 0, titels: [] };
        if (d.verlopen) huidig.verlopen += 1; else huidig.binnenkort += 1;
        huidig.titels.push(`${d.titel} (${new Date(d.geldig_tot).toLocaleDateString("nl-BE")})`);
        map[d.medewerker_id] = huidig;
      });
      setDocWaarschuwingen(map);
    } catch {
      /* stille failure (geen rechten) */
    }
  }, []);

  useEffect(() => { loadDocWaarschuwingen(); }, [loadDocWaarschuwingen]);

  const loadActive = async () => {
    const today = new Date().toISOString().split("T")[0];
    try {
      const r = await axios.get(`${API}/aanwezigheid`, { params: { datum: today } });
      const active = (r.data || []).filter((rec) => !rec.uitklok_tijd && !rec.handmatig);
      setActiveIds(new Set(active.map((r) => r.medewerker_id)));
      setActiveMap(Object.fromEntries(active.map((r) => [r.medewerker_id, r])));
    } catch {
      /* stille failure */
    }
  };

  // Haal actieve (ingeklokte) medewerkers op en refresh elke 30s
  useEffect(() => {
    loadActive();
    const t = setInterval(loadActive, 30000);
    return () => clearInterval(t);
  }, []);

  const startKlok = async (m, filiaalId) => {
    // Als er geen filiaal meegegeven is, open de kies-popover (ook bij 1 filiaal — kiezen is verplicht)
    if (!filiaalId) {
      setSelectFiliaalFor(m.id);
      return;
    }
    setProcessingId(m.id);
    try {
      await axios.post(`${API}/inklokken`, {
        medewerker_id: m.id,
        pincode: m.pincode || "",
        filiaal_id: filiaalId,
      });
      toast.success(`${m.voornaam} ingeklokt`);
      setSelectFiliaalFor(null);
      await loadActive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inklokken mislukt");
    } finally {
      setProcessingId(null);
    }
  };

  const stopKlok = async (m) => {
    setProcessingId(m.id);
    try {
      await axios.post(`${API}/uitklokken`, {
        medewerker_id: m.id,
        pincode: m.pincode || "",
      });
      toast.success(`${m.voornaam} uitgeklokt`);
      await loadActive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uitklokken mislukt");
    } finally {
      setProcessingId(null);
    }
  };

  const togglePin = (id) => setRevealedPins((r) => ({ ...r, [id]: !r[id] }));
  const copyPin = async (m) => {
    if (!m.pincode) return;
    try {
      await navigator.clipboard.writeText(m.pincode);
      toast.success(`PIN gekopieerd voor ${m.voornaam}`);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const filiaalMap = {};
  filialen.forEach((f) => (filiaalMap[f.id] = f.naam));

  const filtered = medewerkers.filter((m) => {
    // Medewerker mag enkel zichzelf zien
    if (isSelfOnly && m.id !== (user?.id || user?.medewerker_id)) return false;
    const matchSearch =
      `${m.voornaam} ${m.achternaam} ${m.email} ${m.functie}`
        .toLowerCase()
        .includes(search.toLowerCase());
    const matchFiliaal =
      filterFiliaal === "all" || (m.filiaal_ids || []).includes(filterFiliaal);
    const matchFunctie = filterFunctie === "all" || (m.functie || "") === filterFunctie;
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "actief" && activeIds.has(m.id)) ||
      (filterStatus === "niet-actief" && !activeIds.has(m.id));
    const matchFiliaalKoppeling =
      filterFiliaalKoppeling === "all" ||
      (filterFiliaalKoppeling === "met" && (m.filiaal_ids || []).length > 0) ||
      (filterFiliaalKoppeling === "zonder" && (m.filiaal_ids || []).length === 0) ||
      (filterFiliaalKoppeling === "meerdere" && (m.filiaal_ids || []).length >= 2);
    const uurloon = m.uurloon || 0;
    const matchLoonMin = uurloonMin === "" || uurloon >= parseFloat(uurloonMin);
    const matchLoonMax = uurloonMax === "" || uurloon <= parseFloat(uurloonMax);
    return matchSearch && matchFiliaal && matchFunctie && matchStatus && matchFiliaalKoppeling && matchLoonMin && matchLoonMax;
  });

  // Unieke functies voor filter dropdown
  const uniekeFuncties = Array.from(new Set(medewerkers.map((m) => m.functie).filter(Boolean))).sort();

  // Aantal actieve filters (voor badge)
  const activeFilterCount =
    (filterFiliaal !== "all" ? 1 : 0) +
    (filterFunctie !== "all" ? 1 : 0) +
    (filterStatus !== "all" ? 1 : 0) +
    (filterFiliaalKoppeling !== "all" ? 1 : 0) +
    (uurloonMin !== "" ? 1 : 0) +
    (uurloonMax !== "" ? 1 : 0);

  const resetAllFilters = () => {
    setSearch("");
    setFilterFiliaal("all");
    setFilterFunctie("all");
    setFilterStatus("all");
    setFilterFiliaalKoppeling("all");
    setUurloonMin("");
    setUurloonMax("");
  };

  // ── Webtoegang (login) per medewerker ──
  const [loginInfo, setLoginInfo] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", wachtwoord: "", rol: "medewerker" });
  const [loginBezig, setLoginBezig] = useState(false);
  const [maakGebruiker, setMaakGebruiker] = useState(false);
  const [pwWijzigen, setPwWijzigen] = useState(false);

  const laadLogin = async (m) => {
    setLoginInfo(null);
    setPwWijzigen(false);
    setLoginForm({ email: m.email || "", wachtwoord: "", rol: "medewerker" });
    try {
      const { data } = await api.get(`/medewerkers/${m.id}/login`);
      setLoginInfo(data);
      setMaakGebruiker(!!data.heeft_login);
      setLoginForm((f) => ({ ...f, email: data.email || m.email || "", rol: data.rol || "medewerker" }));
    } catch { setLoginInfo({ heeft_login: false }); setMaakGebruiker(false); }
  };

  const genereerWachtwoord = () => {
    const tekens = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#";
    let w = "";
    for (let i = 0; i < 12; i++) w += tekens[Math.floor(Math.random() * tekens.length)];
    setLoginForm((f) => ({ ...f, wachtwoord: w }));
  };

  const bewaarLogin = async () => {
    if (!editing) return;
    setLoginBezig(true);
    try {
      const heeftLogin = !!loginInfo?.heeft_login;
      const { data } = await api.post(`/medewerkers/${editing.id}/login`, {
        ...loginForm,
        // Bestaande login: wachtwoord enkel meesturen als je het bewust wijzigt
        wachtwoord: !heeftLogin || pwWijzigen ? loginForm.wachtwoord : "",
      });
      toast.success(data.message || "Webtoegang bewaard");
      setPwWijzigen(false);
      setLoginForm((f) => ({ ...f, wachtwoord: "" }));
      await laadLogin(editing);
    } catch (e) { toast.error(e.response?.data?.detail || "Webtoegang bewaren mislukt"); }
    finally { setLoginBezig(false); }
  };

  const trekLoginIn = async () => {
    if (!editing || !window.confirm("Webtoegang van deze medewerker intrekken?")) return;
    setLoginBezig(true);
    try {
      await api.delete(`/medewerkers/${editing.id}/login`);
      toast.success("Webtoegang ingetrokken");
      await laadLogin(editing);
    } catch (e) { toast.error(e.response?.data?.detail || "Intrekken mislukt"); }
    finally { setLoginBezig(false); }
  };

  const logAlleSessiesUit = async () => {
    if (!loginInfo?.user_id) return;
    setLoginBezig(true);
    try {
      const { data } = await api.post(`/sessies/gebruiker/${loginInfo.user_id}/uitloggen`);
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Uitloggen mislukt"); }
    finally { setLoginBezig(false); }
  };

  const zetApparaatSlot = async (actief) => {
    if (!loginInfo?.user_id) return;
    setLoginBezig(true);
    try {
      const { data } = await api.post(`/sessies/gebruiker/${loginInfo.user_id}/apparaat-slot`, { actief });
      toast.success(data.message);
      await laadLogin(editing);
    } catch (e) { toast.error(e.response?.data?.detail || "Vergrendelen mislukt"); }
    finally { setLoginBezig(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setLoginInfo(null);
    setMaakGebruiker(false);
    setLoginForm({ email: "", wachtwoord: "", rol: "medewerker" });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      ctn_toegang: m.ctn_toegang || "rol",
      voornaam: m.voornaam,
      achternaam: m.achternaam,
      email: m.email || "",
      telefoon: m.telefoon || "",
      uurloon: String(m.uurloon || ""),
      functie: m.functie || "",
      pincode: m.pincode || "",
      filiaal_ids: m.filiaal_ids || [],
      startdatum: m.startdatum || "",
      verlof_recht: String(m.verlof_recht ?? 20),
      adres: m.adres || "",
      rijksregisternr: m.rijksregisternr || "",
      iban: m.iban || "",
      bic: m.bic || "",
      geboortedatum: m.geboortedatum || "",
      nationaliteit: m.nationaliteit || "",
      eid_kaartnummer: m.eid_kaartnummer || "",
      eid_geldig_tot: m.eid_geldig_tot || "",
      eid_foto_voor: m.eid_foto_voor || "",
      eid_foto_achter: m.eid_foto_achter || "",
      rijbewijs_categorieen: m.rijbewijs_categorieen || "",
      rijbewijs_nummer: m.rijbewijs_nummer || "",
      rijbewijs_geldig_tot: m.rijbewijs_geldig_tot || "",
      rijbewijs_foto_voor: m.rijbewijs_foto_voor || "",
      rijbewijs_foto_achter: m.rijbewijs_foto_achter || "",
      kleur: m.kleur || "",
      zondag_extra_per_4u: m.zondag_extra_per_4u ? String(m.zondag_extra_per_4u) : "",
      feestdag_extra_uren: m.feestdag_extra_uren ? String(m.feestdag_extra_uren) : "",
    });
    laadLogin(m);
    setDialogOpen(true);
  };

  const openDelete = (m) => {
    setDeleting(m);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.voornaam.trim() || !form.achternaam.trim()) {
      toast.error("Voornaam en achternaam zijn verplicht");
      return;
    }
    const heeftLogin = !!loginInfo?.heeft_login;
    if (maakGebruiker) {
      const loginEmail = (loginForm.email || form.email || "").trim();
      if (!loginEmail) {
        toast.error("Een e-mailadres is verplicht om een gebruiker aan te maken");
        return;
      }
      if (!heeftLogin && (loginForm.wachtwoord || "").length < 6) {
        toast.error("Vul een wachtwoord in van minstens 6 tekens");
        return;
      }
      if (heeftLogin && pwWijzigen && (loginForm.wachtwoord || "").length < 6) {
        toast.error("Nieuw wachtwoord moet minstens 6 tekens bevatten");
        return;
      }
    }
    if (!maakGebruiker && heeftLogin && !window.confirm("Webtoegang van deze medewerker intrekken?")) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        uurloon: parseFloat(form.uurloon) || 0,
        verlof_recht: parseFloat(form.verlof_recht) || 0,
        zondag_extra_per_4u: parseFloat(form.zondag_extra_per_4u) || 0,
        feestdag_extra_uren: parseFloat(form.feestdag_extra_uren) || 0,
      };
      if (!ctnAccessConfig?.bewerken || form.ctn_toegang === (editing?.ctn_toegang || "rol")) delete payload.ctn_toegang;
      let medewerkerId = editing?.id;
      if (editing) {
        await axios.put(`${API}/medewerkers/${editing.id}`, payload);
        toast.success("Medewerker bijgewerkt");
      } else {
        const res = await axios.post(`${API}/medewerkers`, payload);
        const newId = res.data?.id;
        medewerkerId = newId;
        toast.success("Medewerker toegevoegd", {
          action: newId
            ? {
                label: "→ Arbeidscontract aanmaken",
                onClick: () => navigate(`/contracten?nieuwe-voor=${newId}`),
              }
            : undefined,
          duration: 8000,
        });
      }
      if (medewerkerId && maakGebruiker) {
        try {
          const { data } = await api.post(`/medewerkers/${medewerkerId}/login`, {
            email: (loginForm.email || form.email || "").trim(),
            wachtwoord: !heeftLogin || pwWijzigen ? loginForm.wachtwoord : "",
            rol: loginForm.rol,
          });
          toast.success(data.message || "Gebruiker aangemaakt — zichtbaar bij Gebruikers");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Gebruiker aanmaken mislukt");
        }
      } else if (medewerkerId && !maakGebruiker && heeftLogin) {
        try {
          await api.delete(`/medewerkers/${medewerkerId}/login`);
          toast.success("Webtoegang ingetrokken");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Intrekken mislukt");
        }
      }
      setDialogOpen(false);
      window.dispatchEvent(new Event("ctn-access-updated"));
      onRefresh();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/medewerkers/${deleting.id}`);
      toast.success("Medewerker verwijderd");
      setDeleteDialogOpen(false);
      onRefresh();
    } catch (e) {
      toast.error("Fout bij verwijderen");
    }
  };

  const toggleFiliaal = (fid) => {
    setForm((prev) => ({
      ...prev,
      filiaal_ids: prev.filiaal_ids.includes(fid)
        ? prev.filiaal_ids.filter((id) => id !== fid)
        : [...prev.filiaal_ids, fid],
    }));
  };

  if (loading) {
    return (
      <div data-testid="medewerkers-loading" className="space-y-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded-sm" />
        <div className="h-96 bg-muted animate-pulse rounded-sm" />
      </div>
    );
  }


  const printLijst = () => {
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const filiaalNamen = (m) =>
      (m.filiaal_ids || [])
        .map((id) => filialen.find((f) => f.id === id)?.naam)
        .filter(Boolean)
        .join(", ");
    const kolommen = [
      ["Naam", (m) => `${m.voornaam} ${m.achternaam}`],
      ["Functie", (m) => m.functie],
      ["Status", (m) => (m.actief === false ? "Niet actief" : "Actief")],
      ["Pincode", (m) => m.pincode],
      ["Telefoon", (m) => m.telefoon],
      ["E-mail", (m) => m.email],
      ["Geboortedatum", (m) => m.geboortedatum],
      ["Rijksregisternr", (m) => m.rijksregisternr],
      ["Adres", (m) => m.adres],
      ["Nationaliteit", (m) => m.nationaliteit],
      ["IBAN", (m) => m.iban],
      ["Startdatum", (m) => m.startdatum],
      ["Verlof", (m) => m.verlof_recht],
      ...(canLonen ? [["Uurloon", (m) => (m.uurloon ? `EUR ${Number(m.uurloon).toFixed(2)}` : "")]] : []),
      ["Filialen", filiaalNamen],
    ];
    const vandaag = new Date().toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
    const html = `<!DOCTYPE html><html lang="nl"><head><meta charset="utf-8" />
      <title>Medewerkerslijst</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
        h1 { font-size: 16pt; margin: 0 0 2px; }
        .sub { font-size: 8pt; color: #555; margin-bottom: 10px; }
        .waarschuwing { font-size: 7.5pt; color: #b00; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #999; padding: 3px 4px; font-size: 7pt; text-align: left; vertical-align: top; }
        th { background: #e8eeff; text-transform: uppercase; font-size: 6.5pt; letter-spacing: .04em; }
        tr:nth-child(even) td { background: #fafafa; }
        td.pin { font-weight: bold; letter-spacing: .12em; color: #002FA7; }
      </style></head><body>
      <h1>Medewerkerslijst</h1>
      <div class="sub">${esc(filtered.length)} medewerkers &middot; afgedrukt op ${esc(vandaag)}</div>
      <div class="waarschuwing">Vertrouwelijk — bevat pincodes en persoonsgegevens.</div>
      <table><thead><tr>${kolommen.map(([t]) => `<th>${esc(t)}</th>`).join("")}</tr></thead>
      <tbody>${filtered
        .map(
          (m) =>
            `<tr>${kolommen
              .map(([t, fn]) => `<td class="${t === "Pincode" ? "pin" : ""}">${esc(fn(m))}</td>`)
              .join("")}</tr>`,
        )
        .join("")}</tbody></table>
      <script>window.onload = () => window.print();</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Sta pop-ups toe om de lijst te printen"); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="medewerkers-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Medewerkers</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            <span>{medewerkers.length} medewerker{medewerkers.length !== 1 ? "s" : ""} geregistreerd</span>
            {activeIds.size > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-[#38A169]/10 text-[#38A169] text-xs font-bold" data-testid="active-counter">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#38A169] opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#38A169]"></span>
                </span>
                {activeIds.size} nu ingeklokt
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit && medewerkers.length > 1 && (
            <Button
              onClick={() => setVolgordeOpen(true)}
              variant="outline"
              className="rounded-sm"
              data-testid="volgorde-btn"
            >
              <ArrowsDownUp size={18} weight="bold" className="mr-2" />
              Volgorde
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={printLijst}
              variant="outline"
              className="rounded-sm"
              data-testid="print-medewerkers-btn"
            >
              <Printer size={18} weight="bold" className="mr-2" />
              Print lijst
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={openNew}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="add-medewerker-btn"
            >
              <Plus size={18} weight="bold" className="mr-2" />
              Nieuwe Medewerker
            </Button>
          )}
        </div>
      </div>

      <MedewerkerVolgorde
        open={volgordeOpen}
        onOpenChange={setVolgordeOpen}
        medewerkers={medewerkers}
        onSaved={onRefresh}
      />

      {/* Filters */}
      <div className="space-y-2" data-testid="medewerkers-filters">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Zoek op naam, email of functie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-sm"
              data-testid="search-medewerker-input"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="filter-filiaal-dropdown">
                {filterFiliaal === "all"
                  ? "Alle filialen"
                  : filiaalMap[filterFiliaal] || "Filiaal"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterFiliaal("all")} data-testid="filter-all">
                Alle filialen
              </DropdownMenuItem>
              {filialen.map((f) => (
                <DropdownMenuItem
                  key={f.id}
                  onClick={() => setFilterFiliaal(f.id)}
                  data-testid={`filter-filiaal-${f.id}`}
                >
                  {f.naam}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={() => setMoreOpen((o) => !o)}
            className="rounded-sm flex items-center gap-1.5"
            data-testid="toggle-more-filters"
          >
            ⚙ Meer filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#002FA7] text-white text-[10px] font-bold" data-testid="active-filter-count">
                {activeFilterCount}
              </span>
            )}
          </Button>
          {(search || activeFilterCount > 0) && (
            <Button
              variant="ghost"
              onClick={resetAllFilters}
              className="rounded-sm text-muted-foreground hover:text-foreground"
              data-testid="reset-all-filters"
            >
              × Wis alles
            </Button>
          )}
          <div className="ml-auto self-center text-xs text-muted-foreground" data-testid="filter-count">
            {filtered.length} van {medewerkers.length} medewerkers
          </div>
        </div>

        {moreOpen && (
          <div className="bg-muted/40 border border-border rounded-sm p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="more-filters-panel">
            {/* Functie */}
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Functie / Rol</label>
              <select
                value={filterFunctie}
                onChange={(e) => setFilterFunctie(e.target.value)}
                className="w-full h-8 px-2 border border-border rounded-sm text-sm bg-white"
                data-testid="filter-functie"
              >
                <option value="all">Alle functies</option>
                {uniekeFuncties.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            {/* Status */}
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full h-8 px-2 border border-border rounded-sm text-sm bg-white"
                data-testid="filter-status"
              >
                <option value="all">Iedereen</option>
                <option value="actief">Nu ingeklokt</option>
                <option value="niet-actief">Niet ingeklokt</option>
              </select>
            </div>
            {/* Filiaal koppeling */}
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Filiaal koppeling</label>
              <select
                value={filterFiliaalKoppeling}
                onChange={(e) => setFilterFiliaalKoppeling(e.target.value)}
                className="w-full h-8 px-2 border border-border rounded-sm text-sm bg-white"
                data-testid="filter-filiaal-koppeling"
              >
                <option value="all">Alles</option>
                <option value="met">Met filiaal (≥ 1)</option>
                <option value="zonder">Zonder filiaal</option>
                <option value="meerdere">Meerdere filialen (≥ 2)</option>
              </select>
            </div>
            {/* Uurloon range — alleen met machtiging 'lonen_zien' */}
            {canLonen && (
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Uurloon (€)</label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="min"
                    value={uurloonMin}
                    onChange={(e) => setUurloonMin(e.target.value)}
                    className="h-8 rounded-sm text-sm"
                    data-testid="filter-loon-min"
                  />
                  <span className="text-muted-foreground text-xs">–</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="max"
                    value={uurloonMax}
                    onChange={(e) => setUurloonMax(e.target.value)}
                    className="h-8 rounded-sm text-sm"
                    data-testid="filter-loon-max"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div
          className="bg-white border border-border rounded-sm p-12 text-center text-muted-foreground"
          data-testid="no-medewerkers-found"
        >
          <p className="text-sm">
            {medewerkers.length === 0
              ? "Nog geen medewerkers. Klik op 'Nieuwe Medewerker' om te beginnen."
              : "Geen medewerkers gevonden voor deze zoekopdracht."}
          </p>
        </div>
      ) : (
        <>
        {/* Mobiele kaartweergave */}
        <div className="mob-list" data-testid="medewerkers-mobiel">
          {filtered.map((m) => {
            const fNames = (m.filiaal_ids || []).map((fid) => filiaalMap[fid] || "Onbekend");
            const isActive = activeIds.has(m.id);
            const verlopenDocs = docWaarschuwingen[m.id]?.verlopen || 0;
            const eidVerlopen = m.eid_geldig_tot && new Date(m.eid_geldig_tot) < new Date();
            return (
              <div
                key={m.id}
                className={`mob-card ${eidVerlopen ? "border-[#DC2626]" : isActive ? "border-[#38A169]" : ""}`}
                data-testid={`medewerker-kaart-${m.id}`}
              >
                <div className="mob-card-head">
                  <div className="min-w-0">
                    <p className="mob-title flex items-center gap-1.5">
                      {isActive && <span className="inline-flex h-2 w-2 rounded-full bg-[#38A169] shrink-0" />}
                      <span className={eidVerlopen ? "text-[#DC2626]" : ""}>{m.voornaam} {m.achternaam}</span>
                      {m.heeft_login && (
                        <Badge className="bg-[#002FA7] text-white hover:bg-[#002FA7] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4 uppercase shrink-0" title="Heeft webtoegang (login)" data-testid={`mob-gebruiker-badge-${m.id}`}>
                          {m.login_rol || "gebruiker"}
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{m.functie || "-"}{m.email ? ` · ${m.email}` : ""}</p>
                  </div>
                  {canLonen && <span className="mob-value shrink-0">{"\u20AC"}{(m.uurloon || 0).toFixed(2)}</span>}
                </div>

                <div className="flex flex-wrap gap-1 mt-2">
                  {isActive && <Badge className="bg-[#38A169] text-white hover:bg-[#38A169] text-[9px] font-bold px-1.5 py-0 h-4">ACTIEF</Badge>}
                  {eidVerlopen && <Badge className="bg-[#DC2626] text-white hover:bg-[#DC2626] text-[9px] font-bold px-1.5 py-0 h-4">ID VERVALLEN</Badge>}
                  {verlopenDocs > 0 && <Badge className="bg-[#DC2626] text-white hover:bg-[#DC2626] text-[9px] font-bold px-1.5 py-0 h-4">{verlopenDocs} DOC VERVALLEN</Badge>}
                  {fNames.map((naam, idx) => <Badge key={idx} variant="outline" className="text-[10px]">{naam}</Badge>)}
                </div>

                <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-border">
                  {canEdit && m.pincode ? (
                    <button
                      type="button"
                      onClick={() => togglePin(m.id)}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border font-mono text-xs"
                      data-testid={`mob-toggle-pin-${m.id}`}
                    >
                      {revealedPins[m.id]
                        ? <span className="font-bold tracking-widest text-[#002FA7]">{m.pincode}</span>
                        : <span className="tracking-widest">••••••</span>}
                      {revealedPins[m.id] ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="bold" />}
                    </button>
                  ) : <span className="text-[10px] text-muted-foreground">{m.startdatum || ""}</span>}

                  <div className="flex items-center gap-1.5">
                    {(canKlokAnderen || (isSelfOnly && m.id === (user?.id || user?.medewerker_id))) && (
                      isActive ? (
                        <button
                          onClick={() => stopKlok(m)}
                          disabled={processingId === m.id}
                          className="px-2.5 py-1 rounded-sm bg-[#E53E3E] text-white text-[10px] font-bold uppercase disabled:opacity-50"
                          data-testid={`mob-stop-klok-${m.id}`}
                        >
                          Stop
                        </button>
                      ) : (
                        <button
                          onClick={() => startKlok(m)}
                          disabled={processingId === m.id || !m.pincode}
                          className="px-2.5 py-1 rounded-sm bg-[#38A169] text-white text-[10px] font-bold uppercase disabled:opacity-40"
                          data-testid={`mob-start-klok-${m.id}`}
                        >
                          Start
                        </button>
                      )
                    )}
                    {canEdit && (
                      <>
                        <button onClick={() => openEdit(m)} className="p-1.5 rounded-sm border border-border" data-testid={`mob-edit-medewerker-${m.id}`}>
                          <PencilSimple size={15} />
                        </button>
                        <button onClick={() => openDelete(m)} className="p-1.5 rounded-sm border border-border text-destructive" data-testid={`mob-delete-medewerker-${m.id}`}>
                          <Trash size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {selectFiliaalFor === m.id && (
                  <div className="mt-2 border border-border rounded-sm p-1.5" data-testid={`mob-select-filiaal-${m.id}`}>
                    <div className="text-[10px] font-semibold text-muted-foreground px-1 pb-1 uppercase">Kies filiaal:</div>
                    {filialen.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => startKlok(m, f.id)}
                        className="block w-full text-left px-2 py-2 text-xs rounded-sm hover:bg-muted"
                        data-testid={`mob-pick-filiaal-${m.id}-${f.id}`}
                      >
                        {f.naam}
                      </button>
                    ))}
                    <button onClick={() => setSelectFiliaalFor(null)} className="block w-full text-left px-2 py-1 text-[10px] text-muted-foreground">Annuleren</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="hidden md:block bg-white border border-border rounded-sm overflow-x-auto" data-testid="medewerkers-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Naam</th>
                <th>Email</th>
                <th>Functie</th>
                {canEdit && <th className="w-28">Gebruiker</th>}
                {canLonen && <th>Uurloon</th>}
                <th>Filialen</th>
                <th>Startdatum</th>
                {canEdit && <th className="w-28">Pincode</th>}
                <th className="w-20">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const fNames = (m.filiaal_ids || []).map(
                  (fid) => filiaalMap[fid] || "Onbekend"
                );
                const isActive = activeIds.has(m.id);
                // eID status
                let eidStatus = null; // null | "expired" | "soon"
                if (m.eid_geldig_tot) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const eidDate = new Date(m.eid_geldig_tot);
                  const diffDays = Math.floor((eidDate - today) / 86400000);
                  if (diffDays < 0) eidStatus = "expired";
                  else if (diffDays <= 60) eidStatus = "soon";
                }
                // Rijbewijs status (alleen tonen als chauffeur of rijbewijs ingevuld)
                const isChauffeur = (m.functie || "").toLowerCase().includes("chauffeur");
                let rijStatus = null;
                if (m.rijbewijs_geldig_tot) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const rijDate = new Date(m.rijbewijs_geldig_tot);
                  const diffDays = Math.floor((rijDate - today) / 86400000);
                  if (diffDays < 0) rijStatus = "expired";
                  else if (diffDays <= 60) rijStatus = "soon";
                }
                const rowClass = (eidStatus === "expired" || rijStatus === "expired")
                  ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]"
                  : (isActive ? "bg-[#38A169]/10 hover:bg-[#38A169]/15" : "");
                return (
                  <tr
                    key={m.id}
                    data-testid={`medewerker-row-${m.id}`}
                    className={rowClass}
                    data-active={isActive ? "true" : "false"}
                  >
                    <td className="font-medium whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isActive ? (
                          <span className="relative flex h-2.5 w-2.5 shrink-0" title="Op dit moment ingeklokt">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#38A169] opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#38A169]"></span>
                          </span>
                        ) : (
                          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-steel-200 shrink-0"></span>
                        )}
                        <span className={eidStatus === "expired" ? "text-[#DC2626] font-bold" : ""}>
                          {m.voornaam} {m.achternaam}
                        </span>
                        {m.heeft_login && (
                          <UserCircleGear
                            size={15}
                            weight="fill"
                            className="text-[#002FA7] shrink-0"
                            title="Heeft webtoegang (login)"
                            data-testid={`login-icoon-${m.id}`}
                          />
                        )}
                        {isActive && (
                          <Badge className="bg-[#38A169] text-white hover:bg-[#38A169] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4" data-testid={`actief-badge-${m.id}`}>
                            ACTIEF
                          </Badge>
                        )}
                        {eidStatus === "expired" && (
                          <Badge
                            className="bg-[#DC2626] text-white hover:bg-[#DC2626] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={`eID verlopen op ${new Date(m.eid_geldig_tot).toLocaleDateString('nl-BE')}`}
                            data-testid={`eid-expired-${m.id}`}
                          >
                            ID VERVALLEN — NU INLEZEN
                          </Badge>
                        )}
                        {eidStatus === "soon" && (
                          <Badge
                            className="bg-[#F59E0B] text-white hover:bg-[#F59E0B] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={`eID verloopt op ${new Date(m.eid_geldig_tot).toLocaleDateString('nl-BE')}`}
                            data-testid={`eid-soon-${m.id}`}
                          >
                            ID BIJNA VERVALLEN
                          </Badge>
                        )}
                        {docWaarschuwingen[m.id]?.verlopen > 0 && (
                          <Badge
                            className="bg-[#DC2626] text-white hover:bg-[#DC2626] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={docWaarschuwingen[m.id].titels.join(" · ")}
                            data-testid={`doc-expired-${m.id}`}
                          >
                            📎 {docWaarschuwingen[m.id].verlopen} DOC VERVALLEN
                          </Badge>
                        )}
                        {!docWaarschuwingen[m.id]?.verlopen && docWaarschuwingen[m.id]?.binnenkort > 0 && (
                          <Badge
                            className="bg-[#F59E0B] text-white hover:bg-[#F59E0B] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={docWaarschuwingen[m.id].titels.join(" · ")}
                            data-testid={`doc-soon-${m.id}`}
                          >
                            📎 DOC VERLOOPT
                          </Badge>
                        )}
                        {isChauffeur && !rijStatus && m.rijbewijs_geldig_tot && (
                          <Badge
                            className="bg-[#002FA7] text-white hover:bg-[#002FA7] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={`Rijbewijs ${m.rijbewijs_categorieen} geldig tot ${new Date(m.rijbewijs_geldig_tot).toLocaleDateString('nl-BE')}`}
                          >
                            🚗 {m.rijbewijs_categorieen || "RIJBEWIJS"}
                          </Badge>
                        )}
                        {rijStatus === "expired" && (
                          <Badge
                            className="bg-[#DC2626] text-white hover:bg-[#DC2626] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={`Rijbewijs verlopen op ${new Date(m.rijbewijs_geldig_tot).toLocaleDateString('nl-BE')}`}
                            data-testid={`rij-expired-${m.id}`}
                          >
                            🚗 RIJBEWIJS VERVALLEN
                          </Badge>
                        )}
                        {rijStatus === "soon" && (
                          <Badge
                            className="bg-[#F59E0B] text-white hover:bg-[#F59E0B] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4"
                            title={`Rijbewijs verloopt op ${new Date(m.rijbewijs_geldig_tot).toLocaleDateString('nl-BE')}`}
                            data-testid={`rij-soon-${m.id}`}
                          >
                            🚗 RIJBEWIJS BIJNA VERVALLEN
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-muted-foreground">{m.email || "-"}</td>
                    <td>{m.functie || "-"}</td>
                    {canEdit && (
                      <td data-testid={`gebruiker-cel-${m.id}`}>
                        {m.heeft_login ? (
                          <Badge className="bg-[#002FA7] text-white hover:bg-[#002FA7] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4 uppercase" data-testid={`gebruiker-badge-${m.id}`}>
                            {m.login_rol || "gebruiker"}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                    )}
                    {canLonen && <td className="font-semibold">{"\u20AC"}{(m.uurloon || 0).toFixed(2)}</td>}
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {fNames.length > 0 ? (
                          fNames.map((name, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </td>
                    <td className="text-muted-foreground">{m.startdatum || "-"}</td>
                    {canEdit && (
                      <td>
                        {m.pincode ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => togglePin(m.id)}
                              data-testid={`toggle-pin-${m.id}`}
                              title={revealedPins[m.id] ? "Verberg pincode" : "Toon pincode"}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border hover:bg-muted transition-colors font-mono text-sm tabular-nums"
                            >
                              {revealedPins[m.id] ? (
                                <>
                                  <span className="font-bold tracking-widest text-[#002FA7]">{m.pincode}</span>
                                  <EyeSlash size={14} weight="bold" className="text-muted-foreground" />
                                </>
                              ) : (
                                <>
                                  <span className="tracking-widest">••••••</span>
                                  <Eye size={14} weight="bold" className="text-muted-foreground" />
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => copyPin(m)}
                              data-testid={`copy-pin-${m.id}`}
                              title="Kopieer pincode"
                              className="p-1.5 rounded-sm hover:bg-muted transition-colors text-muted-foreground hover:text-[#002FA7]"
                            >
                              <Copy size={14} weight="bold" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                    )}
                    <td>
                      <div className="flex gap-1 items-center">
                        {/* Start/Stop knoppen — admin+manager mogen iedereen, medewerker alleen zichzelf */}
                        {(canKlokAnderen || (isSelfOnly && m.id === (user?.id || user?.medewerker_id))) && (
                          <>
                            {isActive ? (
                              <button
                                onClick={() => stopKlok(m)}
                                disabled={processingId === m.id}
                                className="px-2 py-1 rounded-sm bg-[#E53E3E] hover:bg-[#C53030] text-white text-[10px] font-bold tracking-wider uppercase transition-colors disabled:opacity-50 flex items-center gap-1"
                                data-testid={`stop-klok-${m.id}`}
                                title="Uitklokken"
                              >
                                <SignOut size={12} weight="bold" /> STOP
                              </button>
                            ) : (
                              <div className="relative">
                                <button
                                  onClick={() => startKlok(m)}
                                  disabled={processingId === m.id || !m.pincode}
                                  className="px-2 py-1 rounded-sm bg-[#38A169] hover:bg-[#2F855A] text-white text-[10px] font-bold tracking-wider uppercase transition-colors disabled:opacity-40 flex items-center gap-1"
                                  data-testid={`start-klok-${m.id}`}
                                  title={!m.pincode ? "Geen pincode ingesteld" : "Inklokken — kies filiaal"}
                                >
                                  <SignIn size={12} weight="bold" /> START
                                </button>
                                {selectFiliaalFor === m.id && (
                                  <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-border rounded-sm shadow-lg p-1.5 min-w-[180px] max-h-72 overflow-y-auto" data-testid={`select-filiaal-${m.id}`}>
                                    <div className="text-[10px] font-semibold text-muted-foreground px-2 py-1 uppercase tracking-wider">Kies filiaal:</div>
                                    {(m.filiaal_ids || []).map((fid) => (
                                      <button
                                        key={fid}
                                        onClick={() => startKlok(m, fid)}
                                        className="block w-full text-left px-2 py-1.5 text-xs hover:bg-[#38A169]/10 rounded-sm flex items-center justify-between"
                                        data-testid={`pick-filiaal-${m.id}-${fid}`}
                                      >
                                        <span className="font-semibold">{filiaalMap[fid] || "Onbekend"}</span>
                                        <span className="text-[9px] text-[#38A169] font-bold">GEKOPPELD</span>
                                      </button>
                                    ))}
                                    {filialen.filter((f) => !(m.filiaal_ids || []).includes(f.id)).length > 0 && (m.filiaal_ids || []).length > 0 && (
                                      <div className="border-t border-border my-1"></div>
                                    )}
                                    {filialen
                                      .filter((f) => !(m.filiaal_ids || []).includes(f.id))
                                      .map((f) => (
                                        <button
                                          key={f.id}
                                          onClick={() => startKlok(m, f.id)}
                                          className="block w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-sm text-muted-foreground"
                                          data-testid={`pick-filiaal-${m.id}-${f.id}`}
                                        >
                                          {f.naam}
                                        </button>
                                      ))}
                                    <div className="border-t border-border my-1"></div>
                                    <button
                                      onClick={() => setSelectFiliaalFor(null)}
                                      className="block w-full text-left px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted rounded-sm"
                                    >
                                      Annuleren
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {/* Bewerken/verwijderen — alleen admins */}
                        {canEdit && (
                          <>
                            <button
                              onClick={() => openEdit(m)}
                              className="p-1.5 rounded-sm hover:bg-muted transition-colors"
                              data-testid={`edit-medewerker-${m.id}`}
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              onClick={() => openDelete(m)}
                              className="p-1.5 rounded-sm hover:bg-destructive/10 text-destructive transition-colors"
                              data-testid={`delete-medewerker-${m.id}`}
                            >
                              <Trash size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl rounded-sm p-0 max-h-[92vh] flex flex-col gap-0" data-testid="medewerker-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editing ? "Medewerker Bewerken" : "Nieuwe Medewerker"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Pas de gegevens van deze medewerker aan." : "Vul de gegevens in voor de nieuwe medewerker."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 px-6 py-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="voornaam" className="text-xs font-semibold">Voornaam *</Label>
                <Input
                  id="voornaam"
                  value={form.voornaam}
                  onChange={(e) => setForm({ ...form, voornaam: e.target.value })}
                  placeholder="Jan"
                  className="rounded-sm mt-1"
                  data-testid="input-voornaam"
                />
              </div>
              <div>
                <Label htmlFor="achternaam" className="text-xs font-semibold">Achternaam *</Label>
                <Input
                  id="achternaam"
                  value={form.achternaam}
                  onChange={(e) => setForm({ ...form, achternaam: e.target.value })}
                  placeholder="de Vries"
                  className="rounded-sm mt-1"
                  data-testid="input-achternaam"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jan@bedrijf.nl"
                  className="rounded-sm mt-1"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="telefoon" className="text-xs font-semibold">Telefoon</Label>
                <Input
                  id="telefoon"
                  value={form.telefoon}
                  onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
                  placeholder="06-12345678"
                  className="rounded-sm mt-1"
                  data-testid="input-telefoon"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {canLonen && (
                <div>
                  <Label htmlFor="uurloon" className="text-xs font-semibold">Uurloon ({"\u20AC"})</Label>
                  <Input
                    id="uurloon"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.uurloon}
                    onChange={(e) => setForm({ ...form, uurloon: e.target.value })}
                    placeholder="15.00"
                    className="rounded-sm mt-1"
                    data-testid="input-uurloon"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="functie" className="text-xs font-semibold">Functie / Rol</Label>
                <Select value={form.functie} onValueChange={(v) => setForm({ ...form, functie: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="input-functie">
                    <SelectValue placeholder="Selecteer rol..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Medewerker">Medewerker</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-border rounded-sm p-3 bg-muted/20">
              <div className="text-xs font-bold uppercase tracking-wider mb-2">
                Extra uren zondag / feestdag
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="zondag-extra" className="text-xs font-semibold">
                    Zondag: extra uren per 4 gewerkte uren
                  </Label>
                  <Input
                    id="zondag-extra"
                    type="number"
                    step="0.25"
                    min="0"
                    value={form.zondag_extra_per_4u}
                    onChange={(e) => setForm({ ...form, zondag_extra_per_4u: e.target.value })}
                    placeholder="0 = geen toeslag"
                    className="rounded-sm mt-1"
                    data-testid="input-zondag-extra"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Bv. 1 → 4 u werken wordt 5 u, 8 u wordt 10 u (alleen als er gewerkt is)
                  </p>
                </div>
                <div>
                  <Label htmlFor="feestdag-extra" className="text-xs font-semibold">
                    Feestdag: vast aantal extra uren
                  </Label>
                  <Input
                    id="feestdag-extra"
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.feestdag_extra_uren}
                    onChange={(e) => setForm({ ...form, feestdag_extra_uren: e.target.value })}
                    placeholder="0 = geen toeslag"
                    className="rounded-sm mt-1"
                    data-testid="input-feestdag-extra"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Bv. 8 → op elke Belgische feestdag +8 u, ook als er niet gewerkt is. Valt de
                    feestdag op een zondag, dan tellen beide toeslagen op.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Naamkleur in maandoverzicht</Label>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {NAAM_KLEUREN.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kleur: k })}
                    className={`w-7 h-7 rounded-sm border-2 transition-transform hover:scale-110 ${form.kleur === k ? "border-foreground" : "border-border"}`}
                    style={{ backgroundColor: k }}
                    data-testid={`form-kleur-${k.replace("#", "")}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, kleur: "" })}
                  className={`h-7 px-2 text-[10px] font-semibold rounded-sm border-2 ${!form.kleur ? "border-foreground" : "border-border"}`}
                  data-testid="form-kleur-geen"
                >
                  Geen kleur
                </button>
              </div>
            </div>

            {canEdit && (
              <div className="border border-border rounded-sm p-3 bg-muted/20" data-testid="webtoegang-blok">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      className="shrink-0 h-5 w-5"
                      checked={maakGebruiker}
                      onCheckedChange={(v) => {
                        setMaakGebruiker(!!v);
                        if (v && !loginForm.email) setLoginForm((f) => ({ ...f, email: form.email || "" }));
                      }}
                      data-testid="maak-gebruiker-checkbox"
                    />
                    <span className="text-xs font-semibold">Maak deze medewerker gebruiker (webtoegang)</span>
                  </label>
                  {loginInfo?.heeft_login && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#38A169]" data-testid="login-actief-badge">Login actief</span>
                  )}
                </div>
                {maakGebruiker && (<>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-semibold text-muted-foreground">E-mail (loginnaam)</Label>
                    <Input
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="naam@firma.be"
                      className="rounded-sm mt-1 h-9"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold text-muted-foreground">Rol (bepaalt de machtigingen)</Label>
                    <Select value={loginForm.rol} onValueChange={(v) => setLoginForm({ ...loginForm, rol: v })}>
                      <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="login-rol"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="medewerker">Medewerker</SelectItem>
                        <SelectItem value="verkoper">Verkoper</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  {loginInfo?.heeft_login && !pwWijzigen ? (
                    <div className="flex items-center justify-between gap-2 bg-white border border-border rounded-sm px-3 py-2">
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Wachtwoord blijft ongewijzigd
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-sm h-8 text-xs shrink-0"
                        onClick={() => setPwWijzigen(true)}
                        data-testid="login-pw-wijzigen-btn"
                      >
                        Wachtwoord wijzigen
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Label className="text-[10px] font-semibold text-muted-foreground">
                        {loginInfo?.heeft_login ? "Nieuw wachtwoord (min. 6 tekens)" : "Wachtwoord (min. 6 tekens)"}
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="text"
                          value={loginForm.wachtwoord}
                          onChange={(e) => setLoginForm({ ...loginForm, wachtwoord: e.target.value })}
                          placeholder="••••••••"
                          className="rounded-sm h-9"
                          data-testid="login-wachtwoord"
                        />
                        <Button type="button" variant="outline" className="rounded-sm h-9 shrink-0" onClick={genereerWachtwoord} data-testid="login-genereer-btn">
                          Voorstel
                        </Button>
                      </div>
                      {loginInfo?.heeft_login && (
                        <button
                          type="button"
                          onClick={() => { setPwWijzigen(false); setLoginForm((f) => ({ ...f, wachtwoord: "" })); }}
                          className="text-[11px] text-muted-foreground underline mt-1"
                          data-testid="login-pw-annuleren-btn"
                        >
                          Wachtwoord toch niet wijzigen
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {editing && (
                    <Button type="button" onClick={bewaarLogin} disabled={loginBezig} className="rounded-sm h-9 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="login-bewaar-btn">
                      {loginInfo?.heeft_login ? "Login bijwerken" : "Login nu aanmaken"}
                    </Button>
                  )}
                  {loginInfo?.heeft_login && (
                    <Button type="button" variant="outline" onClick={trekLoginIn} disabled={loginBezig} className="rounded-sm h-9 text-destructive" data-testid="login-intrekken-btn">
                      Toegang intrekken
                    </Button>
                  )}
                  {loginInfo?.heeft_login && (
                    <Button type="button" variant="outline" onClick={logAlleSessiesUit} disabled={loginBezig} className="rounded-sm h-9" data-testid="login-sessies-uitloggen-btn">
                      Alle apparaten uitloggen
                    </Button>
                  )}
                  {loginInfo?.heeft_login && (
                    loginInfo.apparaat_vergrendeld ? (
                      <Button type="button" variant="outline" onClick={() => zetApparaatSlot(false)} disabled={loginBezig} className="rounded-sm h-9" data-testid="login-ontgrendel-btn">
                        Apparaatslot uitzetten
                      </Button>
                    ) : (
                      <Button type="button" onClick={() => zetApparaatSlot(true)} disabled={loginBezig} className="rounded-sm h-9 bg-[#C62828] hover:bg-[#A31F1F] text-white" data-testid="login-vergrendel-btn">
                        Vergrendel op huidig apparaat
                      </Button>
                    )
                  )}
                </div>
                {loginInfo?.apparaat_vergrendeld && (
                  <p className="text-[10px] font-semibold text-[#C62828] mt-2" data-testid="apparaat-slot-info">
                    Vergrendeld op: {loginInfo.toegestaan_apparaat_naam || "één apparaat"} — inloggen vanaf andere apparaten is geblokkeerd.
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground mt-2">
                  De rol bepaalt wat deze persoon ziet — pas dat aan via Instellingen → Rol Machtigingen.
                  De gebruiker verschijnt na opslaan automatisch in de gebruikerslijst.
                </p>
                </>)}
                {!maakGebruiker && (
                  <p className="text-[10px] text-muted-foreground">
                    Vink aan om een login aan te maken (e-mail + wachtwoord + rol).
                  </p>
                )}
              </div>
            )}

            {ctnAccessConfig?.bewerken && <CtnEmployeeAccess value={form.ctn_toegang} onChange={(value) => setForm((previous) => ({ ...previous, ctn_toegang: value }))} roleAllowed={ctnAccessConfig.rollen[loginForm.rol] === true} hasLogin={maakGebruiker} disabled={saving} />}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="startdatum" className="text-xs font-semibold">Startdatum</Label>
                <Input
                  id="startdatum"
                  type="date"
                  value={form.startdatum}
                  onChange={(e) => setForm({ ...form, startdatum: e.target.value })}
                  className="rounded-sm mt-1"
                  data-testid="input-startdatum"
                />
              </div>
              <div>
                <Label htmlFor="pincode" className="text-xs font-semibold flex items-center gap-1">
                  <LockKey size={12} /> Pincode
                </Label>
                <Input
                  id="pincode"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                  placeholder="1234"
                  className="rounded-sm mt-1"
                  data-testid="input-pincode"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="verlof_recht" className="text-xs font-semibold">Verlof recht (dagen/jaar)</Label>
              <Input
                id="verlof_recht"
                type="number"
                step="0.5"
                min="0"
                value={form.verlof_recht}
                onChange={(e) => setForm({ ...form, verlof_recht: e.target.value })}
                placeholder="20"
                className="rounded-sm mt-1"
                data-testid="input-verlof-recht"
              />
            </div>

            {/* Contractgegevens */}
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  Contractgegevens (voor arbeidscontracten)
                </h3>
                <label
                  className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] cursor-pointer flex items-center gap-1 px-2 py-1 border border-[#002FA7] rounded-sm hover:bg-[#EAF1FF]"
                  data-testid="med-scan-eid-label"
                >
                  📷 Inlezen ID
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      const fd = new FormData();
                      Array.from(files).forEach((f) => fd.append("images", f));
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contracten/scan-eid${editing?.id ? `?medewerker_id=${editing.id}` : ""}`;
                      try {
                        const res = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        const d = res.data;
                        if (!d.ok) {
                          toast.error(d.detail || "Geen leesbare gegevens gevonden");
                          return;
                        }
                        setForm((prev) => ({
                          ...prev,
                          voornaam: d.voornaam || prev.voornaam,
                          achternaam: d.achternaam || prev.achternaam,
                          adres: d.adres || prev.adres,
                          rijksregisternr: d.rijksregisternr || prev.rijksregisternr,
                          geboortedatum: d.geboortedatum || prev.geboortedatum,
                          nationaliteit: d.nationaliteit || prev.nationaliteit,
                          eid_kaartnummer: d.kaartnummer || prev.eid_kaartnummer || "",
                          eid_geldig_tot: d.geldig_tot || prev.eid_geldig_tot || "",
                          eid_foto_voor: d.eid_foto_voor || prev.eid_foto_voor || "",
                          eid_foto_achter: d.eid_foto_achter || prev.eid_foto_achter || "",
                        }));
                        toast.success(editing?.id ? "eID ingelezen + foto's bewaard" : "eID-gegevens ingelezen");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "eID-scan mislukt");
                      }
                      e.target.value = "";
                    }}
                    data-testid="med-scan-eid-input"
                  />
                </label>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold">Adres</Label>
                  <Input
                    value={form.adres}
                    onChange={(e) => setForm({ ...form, adres: e.target.value })}
                    placeholder="Straat 1, 0000 Stad"
                    className="rounded-sm mt-1"
                    data-testid="input-adres"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Rijksregisternr</Label>
                    <Input
                      value={form.rijksregisternr}
                      onChange={(e) => setForm({ ...form, rijksregisternr: e.target.value })}
                      placeholder="00.00.00-000.00"
                      className="rounded-sm mt-1"
                      data-testid="input-rr"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Geboortedatum</Label>
                    <Input
                      type="date"
                      value={form.geboortedatum}
                      onChange={(e) => setForm({ ...form, geboortedatum: e.target.value })}
                      className="rounded-sm mt-1"
                      data-testid="input-geboorte"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">IBAN</Label>
                    <Input
                      value={form.iban}
                      onChange={(e) => setForm({ ...form, iban: e.target.value })}
                      placeholder="BE.. .... .... ...."
                      className="rounded-sm mt-1"
                      data-testid="input-iban"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">BIC</Label>
                    <Input
                      value={form.bic}
                      onChange={(e) => setForm({ ...form, bic: e.target.value })}
                      placeholder="GEBABEBB"
                      className="rounded-sm mt-1"
                      data-testid="input-bic"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Nationaliteit</Label>
                  <Input
                    value={form.nationaliteit}
                    onChange={(e) => setForm({ ...form, nationaliteit: e.target.value })}
                    placeholder="Belgisch"
                    className="rounded-sm mt-1"
                    data-testid="input-nationaliteit"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">eID kaartnummer</Label>
                    <Input
                      value={form.eid_kaartnummer}
                      onChange={(e) => setForm({ ...form, eid_kaartnummer: e.target.value })}
                      placeholder="000-0000000-00"
                      className="rounded-sm mt-1 font-mono text-xs"
                      data-testid="input-eid-nr"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">eID geldig tot</Label>
                    <Input
                      type="date"
                      value={form.eid_geldig_tot}
                      onChange={(e) => setForm({ ...form, eid_geldig_tot: e.target.value })}
                      className="rounded-sm mt-1"
                      data-testid="input-eid-tot"
                    />
                  </div>
                </div>
                {(form.eid_foto_voor || form.eid_foto_achter) && editing?.id && (
                  <div className="flex gap-3 flex-wrap mt-1">
                    {form.eid_foto_voor && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/contracten/eid-foto/${editing.id}/voor`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid="eid-foto-voor"
                      >
                        🖼️ Foto eID voorzijde
                      </a>
                    )}
                    {form.eid_foto_achter && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/contracten/eid-foto/${editing.id}/achter`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid="eid-foto-achter"
                      >
                        🖼️ Foto eID achterzijde
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Rijbewijs (chauffeurs) */}
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-2">
                  🚗 Rijbewijs
                  {(form.functie || "").toLowerCase().includes("chauffeur") && (
                    <Badge className="bg-[#002FA7] text-white hover:bg-[#002FA7] text-[9px] font-bold tracking-wider px-1.5 py-0 h-4">
                      CHAUFFEUR
                    </Badge>
                  )}
                </h3>
                <label
                  className="text-[10px] font-bold uppercase tracking-wider text-[#16A34A] cursor-pointer flex items-center gap-1 px-2 py-1 border border-[#16A34A] rounded-sm hover:bg-[#F0FAEC]"
                  data-testid="med-scan-rij-label"
                >
                  📷 Inlezen rijbewijs
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      const fd = new FormData();
                      Array.from(files).forEach((f) => fd.append("images", f));
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contracten/scan-rijbewijs${editing?.id ? `?medewerker_id=${editing.id}` : ""}`;
                      try {
                        const res = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        const d = res.data;
                        if (!d.ok) {
                          toast.error(d.detail || "Geen leesbare gegevens gevonden");
                          return;
                        }
                        setForm((prev) => ({
                          ...prev,
                          rijbewijs_categorieen: d.rijbewijs_categorieen || prev.rijbewijs_categorieen,
                          rijbewijs_nummer: d.rijbewijs_nummer || prev.rijbewijs_nummer,
                          rijbewijs_geldig_tot: d.rijbewijs_geldig_tot || prev.rijbewijs_geldig_tot,
                          rijbewijs_foto_voor: d.rijbewijs_foto_voor || prev.rijbewijs_foto_voor,
                          rijbewijs_foto_achter: d.rijbewijs_foto_achter || prev.rijbewijs_foto_achter,
                        }));
                        toast.success("Rijbewijs ingelezen ✓");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Rijbewijs-scan mislukt");
                      }
                      e.target.value = "";
                    }}
                    data-testid="med-scan-rij-input"
                  />
                </label>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Categorieën</Label>
                    <Input
                      value={form.rijbewijs_categorieen}
                      onChange={(e) => setForm({ ...form, rijbewijs_categorieen: e.target.value.toUpperCase() })}
                      placeholder="AM B"
                      className="rounded-sm mt-1 font-mono"
                      data-testid="input-rij-cat"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Nummer</Label>
                    <Input
                      value={form.rijbewijs_nummer}
                      onChange={(e) => setForm({ ...form, rijbewijs_nummer: e.target.value })}
                      className="rounded-sm mt-1 font-mono text-xs"
                      data-testid="input-rij-nr"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Geldig tot</Label>
                    <Input
                      type="date"
                      value={form.rijbewijs_geldig_tot}
                      onChange={(e) => setForm({ ...form, rijbewijs_geldig_tot: e.target.value })}
                      className="rounded-sm mt-1"
                      data-testid="input-rij-tot"
                    />
                  </div>
                </div>
                {(form.rijbewijs_foto_voor || form.rijbewijs_foto_achter) && editing?.id && (
                  <div className="flex gap-3 flex-wrap">
                    {form.rijbewijs_foto_voor && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/contracten/rijbewijs-foto/${editing.id}/voor`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                      >
                        🖼️ Foto voorzijde
                      </a>
                    )}
                    {form.rijbewijs_foto_achter && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/contracten/rijbewijs-foto/${editing.id}/achter`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                      >
                        🖼️ Foto achterzijde
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Documentenkluis — alleen bij bewerken */}
            {editing?.id && (
              <div className="pt-3 border-t border-border">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">
                  📎 Documenten van deze medewerker
                </h3>
                <DocumentenKluis
                  medewerkerId={editing.id}
                  form={form}
                  onFotoVerwijderd={(soort, kant) =>
                    setForm((prev) => ({ ...prev, [`${soort === "eid" ? "eid" : "rijbewijs"}_foto_${kant}`]: "" }))
                  }
                  onAiGegevens={(documenten) => {
                    const velden = {};
                    documenten.forEach((d) => {
                      if (d.type === "idkaart") {
                        if (d.geldig_tot) velden.eid_geldig_tot = d.geldig_tot;
                        if (d.nummer && /^\d{3}-\d{7}-\d{2}$/.test(d.nummer)) velden.eid_kaartnummer = d.nummer;
                      }
                      if (d.type === "rijbewijs") {
                        if (d.geldig_tot) velden.rijbewijs_geldig_tot = d.geldig_tot;
                        if (d.nummer) velden.rijbewijs_nummer = d.nummer;
                      }
                    });
                    if (Object.keys(velden).length === 0) {
                      toast.info("Geen datum of nummer gevonden om over te nemen");
                      return;
                    }
                    setForm((prev) => ({ ...prev, ...velden }));
                    toast.success("Gegevens overgenomen — klik op Bijwerken om te bewaren");
                  }}
                />
              </div>
            )}

            {/* Arbeidscontracten — alleen bij bewerken */}
            {editing?.id && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                    📄 Arbeidscontracten ({medContracten.length})
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setDialogOpen(false);
                      navigate(`/contracten?nieuwe-voor=${editing.id}`);
                    }}
                    className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] cursor-pointer flex items-center gap-1 px-2 py-1 border border-[#002FA7] rounded-sm hover:bg-[#EAF1FF]"
                    data-testid="med-new-contract-btn"
                  >
                    + Nieuw contract
                  </button>
                </div>
                {medContracten.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic py-2">
                    Nog geen arbeidscontracten voor deze medewerker.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {medContracten.map((c) => {
                      const colors = {
                        actief: "#16A34A",
                        verlopen: "#DC2626",
                        concept: "#94A3B8",
                        beeindigd: "#525252",
                      };
                      return (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-2 px-2 py-1.5 bg-muted/30 border border-border rounded-sm text-xs"
                          data-testid={`contract-${c.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <span
                              className="inline-block px-1.5 py-0 rounded-sm text-[9px] font-bold uppercase tracking-wider text-white mr-2"
                              style={{ backgroundColor: colors[c.status] || colors.concept }}
                            >
                              {c.status}
                            </span>
                            <span className="font-mono">{c.datum_van}</span>
                            {c.datum_tot ? (
                              <span className="text-muted-foreground"> → {c.datum_tot}</span>
                            ) : (
                              <span className="text-muted-foreground italic"> (onbepaald)</span>
                            )}
                            <span className="text-muted-foreground"> · {Number(c.uren_per_week || 0)}u/w · €{Number(c.bruto_per_uur || 0).toFixed(2).replace(".", ",")}/u</span>
                          </div>
                          <a
                            href={metFirma(`${process.env.REACT_APP_BACKEND_URL}/api/contracten/${c.id}/pdf`)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#002FA7] hover:underline text-[10px] font-bold whitespace-nowrap"
                            title="Download PDF"
                          >
                            📄 PDF
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              setDialogOpen(false);
                              navigate(`/contracten`);
                            }}
                            className="text-[#002FA7] hover:underline text-[10px] font-bold whitespace-nowrap"
                          >
                            ✎ Beheer
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!form.email && (
                  <p className="text-[10px] text-[#F59E0B] italic mt-2">
                    ⚠ Vul een e-mailadres in om contracten direct te kunnen versturen.
                  </p>
                )}
              </div>
            )}

            {filialen.length > 0 && (
              <div>
                <Label className="text-xs font-semibold mb-2 block">Filialen</Label>
                <div className="border border-border rounded-sm p-3 space-y-2 max-h-40 overflow-y-auto" data-testid="filiaal-checkboxes">
                  {filialen.map((f) => (
                    <div key={f.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`fil-${f.id}`}
                        checked={form.filiaal_ids.includes(f.id)}
                        onCheckedChange={() => toggleFiliaal(f.id)}
                        data-testid={`checkbox-filiaal-${f.id}`}
                      />
                      <label
                        htmlFor={`fil-${f.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {f.naam}
                        {f.stad && (
                          <span className="text-muted-foreground ml-1">({f.stad})</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-3 border-t border-border bg-muted/30 shrink-0">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-sm"
              data-testid="cancel-medewerker-btn"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="save-medewerker-btn"
            >
              {saving ? "Opslaan..." : editing ? "Bijwerken" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="delete-medewerker-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Medewerker Verwijderen
            </DialogTitle>
            <DialogDescription>
              Weet u zeker dat u{" "}
              <strong>
                {deleting?.voornaam} {deleting?.achternaam}
              </strong>{" "}
              wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="rounded-sm"
              data-testid="cancel-delete-btn"
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="rounded-sm"
              data-testid="confirm-delete-btn"
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
